import { OrbitControls } from "@react-three/drei";
import { Canvas } from "@react-three/fiber";
import { useAtom } from "jotai";
import React, {
  type CSSProperties,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import * as THREE from "three";
import { v4 as uuidv4 } from "uuid";
import { ROUTE } from "~/constants/routes";
import "~/css/reception/ReceptionStandbyScreen.css";
import { useTranslation } from "react-i18next";
import type { Character } from "shared/Schemas";
import {
  aivisspeechWarmupCompletedAtom,
  characterIndexAtom,
  currentSpeakerAtom,
  currentVRMAtom,
  difyWarmupCompletedAtom,
  faceModelLoadedAtom,
  isAllLoadedAtom,
  isFaceAtom,
  isFaceDetectionAtom,
  isListeningAtom,
  isProgressModalReadyAtom,
  isStandbyModeAtom,
  isThinkingAtom,
  languageAtom,
  model3dLoadedAtom,
  motion3dLoadedAtom,
  receptionButtonTypeAtom,
  receptionCompanyIdAtom,
  T2SEngineAtom,
  voicevoxWarmupCompletedAtom,
  volumeAtom,
  vrmListAtom,
  vrmPathAtom,
} from "~/Atom";
import { currentLocale, getNameAndStyle } from "~/Commons";
import * as db from "~/components/db/CharacterDBUtils";
import FaceDetection from "~/components/FaceDetection";
import { usePlayVoiceWithCooldown } from "~/components/hooks/usePlayVoiceWithCooldown";
import { useProgressModal } from "~/components/hooks/useProgressModal";
import { useReceptionStandby } from "~/components/hooks/useReceptionStandby";
import ProgressModal from "~/components/modal/ProgressModal";
import ReceptionErrorModal from "~/components/modal/ReceptionErrorModal";
import ReceptionCommunicatingIndicator from "~/components/ReceptionCommunicatingIndicator";
import ReceptionStandbyFooter from "~/components/reception/ReceptionStandbyFooter";
import FitText from "~/components/reception/FitText";
import * as icon from "~/Icons";
import Model from "~/Model";
import { receptionBowEndExpression } from "~/reception/motion/receptionBowMotion";
import { createReceptionIdleMotionStrategy } from "~/reception/motion/receptionIdleMotion";
import { PlaySound } from "~/SoundUtils";
import { extractInfo } from "~/TTS/SpeakersList";
import { warmupDifyConnection } from "~/utils/DifyWarmup";
import { warmupAivisSpeech, warmupVoicevox } from "~/utils/VoiceWarmup";
import { getVRMName } from "~/vrm_utils/VRMUtils";

/** 3Dキャンバスのスタイル */
const canvasStyle: CSSProperties = {
  display: "block",
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
};

/** カメラ設定 */
const cameraConfig = {
  fov: 20,
  near: 0.1,
  far: 100,
  position: [0, -0.04, 0.5] as [number, number, number],
};

/** ライティング設定 */
const lighting = {
  directionalLight: {
    position: {
      x: 5,
      y: 10,
      z: 10,
    },
    color: 0xffffff,
    intensity: 3.0,
  },
  ambientLight: { color: 0xffffff, intensity: 0.5 },
};

/** GL設定 */
const glConfig = {
  antialias: true,
  alpha: true,
  outputColorSpace: THREE.SRGBColorSpace,
  toneMapping: THREE.NeutralToneMapping,
  toneMappingExposure: 1.0,
};

/** キャラクター切り替え方向 */
const characterDirection = {
  next: 1,
  previous: -1,
} as const;

type DirectionType = typeof characterDirection.next | typeof characterDirection.previous;

interface ReceptionStandbyScreenProps {
  registerPageNavigationHandler?: (handler: (() => void) | null) => void;
  skipStartupModal?: boolean;
}

// ============================================================
// コンポーネント
// ============================================================

const ReceptionStandbyScreen: React.FC<ReceptionStandbyScreenProps> = ({
  registerPageNavigationHandler,
  skipStartupModal = false,
  // eslint-disable-next-line complexity
}) => {
  const { t } = useTranslation();
  const [isReceptionErrorModalOpen, setIsReceptionErrorModalOpen] = useState(false);

  // 受付専用の待機モーションストラテジー。
  // sequential モードの巡回 index を保持するため、useMemo で 1 回だけ生成する。
  const idleMotionStrategy = useMemo(() => createReceptionIdleMotionStrategy(), []);

  // ------ State / Atoms ------
  const [, setIsStandbyMode] = useAtom(isStandbyModeAtom);
  const [isFace] = useAtom(isFaceAtom);
  const [isFaceDetection] = useAtom(isFaceDetectionAtom);
  const [currentVRM] = useAtom(currentVRMAtom);
  const [volume, setVolume] = useAtom(volumeAtom);
  const [, setT2SEngine] = useAtom(T2SEngineAtom);
  const [, setCurrentSpeaker] = useAtom(currentSpeakerAtom);
  const [isListening] = useAtom(isListeningAtom);
  const [isThinking] = useAtom(isThinkingAtom);
  const [, setReceptionButtonType] = useAtom(receptionButtonTypeAtom);
  const [, setReceptionCompanyId] = useAtom(receptionCompanyIdAtom);

  // ------ VRM切り替え用 Atoms ------
  const [, setCharacterIndex] = useAtom(characterIndexAtom);
  const [vrmList] = useAtom(vrmListAtom);
  const [, setVRMPath] = useAtom(vrmPathAtom);
  const [language] = useAtom(languageAtom);
  const [isAllLoaded] = useAtom(isAllLoadedAtom);
  const [canvasKey, setCanvasKey] = useState(0);

  // ボタン無効化条件
  const isControlDisabled = (isListening && isThinking) || !isAllLoaded;

  // ------ ProgressModal用 Atoms ------
  const [, setIsProgressModalReady] = useAtom(isProgressModalReadyAtom);
  const [isFaceModelLoaded] = useAtom(faceModelLoadedAtom);
  const [isModel3dLoaded] = useAtom(model3dLoadedAtom);
  const [isMotion3dLoaded] = useAtom(motion3dLoadedAtom);
  const [isDifyWarmupCompleted, setIsDifyWarmupCompleted] = useAtom(difyWarmupCompletedAtom);
  const [isVoicevoxWarmupCompleted, setIsVoicevoxWarmupCompleted] = useAtom(
    voicevoxWarmupCompletedAtom
  );
  const [isAivisspeechWarmupCompleted, setIsAivisspeechWarmupCompleted] = useAtom(
    aivisspeechWarmupCompletedAtom
  );

  const {
    isProgressModalOpen,
    progressValue,
    errorMessage: progressErrorMessage,
    openProgressModal,
    closeProgressModal,
    completedProcessKeys,
    completeProcess,
    setErrorMessage,
  } = useProgressModal();
  const warmupVoicevoxExecutedRef = useRef(false);
  const warmupAivisSpeechExecutedRef = useRef(false);
  const warmupDifyExecutedRef = useRef(false);

  // ------ Reception Standby Hook ------
  const {
    isLoading,
    activationErrorCode,
    errorMessage,
    receptionErrorMessage,
    canNavigate,
    activationDataStatus,
    stopPolling,
    phoneBookMeta,
  } = useReceptionStandby();

  const hasNavigatedRef = useRef<boolean>(false);

  const navigate = useNavigate();

  const handleNavigateToNext = useCallback(() => {
    if (!canNavigate) return;
    if (hasNavigatedRef.current) return;

    hasNavigatedRef.current = true;

    try {
      if (!phoneBookMeta) {
        hasNavigatedRef.current = false;
        return;
      }

      if (phoneBookMeta.isSelectCompanyEnabled) {
        navigate(ROUTE.RECEPTION.COMPANY);
      } else {
        navigate(ROUTE.RECEPTION.MENU);
      }
    } catch (err) {
      hasNavigatedRef.current = false;
      console.error("navigation error:", err);
    }
  }, [canNavigate, phoneBookMeta, navigate]);

  useEffect(() => {
    return () => {
      hasNavigatedRef.current = false;
    };
  }, []);

  useEffect(() => {
    registerPageNavigationHandler?.(handleNavigateToNext);

    return () => {
      registerPageNavigationHandler?.(null);
    };
  }, [registerPageNavigationHandler, handleNavigateToNext]);

  useEffect(() => {
    setReceptionCompanyId(0);
    setReceptionButtonType("");
  }, [setReceptionCompanyId, setReceptionButtonType]);

  /**
   * スタンバイスクリーン初期化完了時に準備完了フラグをセット
   */
  useEffect(() => {
    setIsProgressModalReady(true);
    return () => {
      setIsProgressModalReady(false);
    };
  }, [setIsProgressModalReady]);

  // ------ ProgressModal: プロセス登録 + warmup ------
  useEffect(() => {
    // 動的にプロセスキーを構築（条件に応じて登録）
    const processKeys: string[] = [
      "3d_model",
      "3d_motion",
      "face_model",
      "dify_warmup",
      "voicevox_warmup",
      "aivisspeech_warmup",
      "activation_and_data",
    ];
    if (!skipStartupModal) {
      openProgressModal(processKeys);
    }

    if (!warmupVoicevoxExecutedRef.current) {
      warmupVoicevoxExecutedRef.current = true;
      warmupVoicevox()
        .then(() => setIsVoicevoxWarmupCompleted(true))
        .catch((error) => {
          console.error("VOICEVOX warmup error:", error);
          setErrorMessage(t("systemStartupError"));
          warmupVoicevoxExecutedRef.current = false;
        });
    }

    if (!warmupAivisSpeechExecutedRef.current) {
      warmupAivisSpeechExecutedRef.current = true;
      warmupAivisSpeech()
        .then(() => setIsAivisspeechWarmupCompleted(true))
        .catch((error) => {
          console.error("AivisSpeech warmup error:", error);
          setErrorMessage(t("systemStartupError"));
          warmupAivisSpeechExecutedRef.current = false;
        });
    }

    // Difyウォームアップを実行
    if (!warmupDifyExecutedRef.current) {
      warmupDifyExecutedRef.current = true;
      const sessionId =
        sessionStorage.getItem("aiConciergeSessionId") ||
        (() => {
          const newId = uuidv4();
          sessionStorage.setItem("aiConciergeSessionId", newId);
          return newId;
        })();
      warmupDifyConnection(sessionId)
        .then(() => {
          setIsDifyWarmupCompleted(true);
        })
        .catch((error) => {
          console.error("Dify warmup error:", error);
          setErrorMessage(t("systemStartupError"));
          warmupDifyExecutedRef.current = false;
        });
    }
  }, [
    skipStartupModal,
    openProgressModal,
    setIsVoicevoxWarmupCompleted,
    setIsAivisspeechWarmupCompleted,
    setIsDifyWarmupCompleted,
    setErrorMessage,
    t,
  ]);
  /**
   * モーダルの表示状態に応じてスタンバイモードを設定
   */
  useEffect(() => {
    setIsStandbyMode(true);
    // Canvas再マウントしてcameraプロパティを反映させる
    setCanvasKey((prev) => prev + 1);
    return () => {
      setIsStandbyMode(false);
    };
  }, [setIsStandbyMode]);

  // ------ ProgressModal: 各Atomを監視して完了通知 ------
  useEffect(() => {
    if (!isProgressModalOpen) return;

    const loadedStatus = [
      { isLoaded: isFaceModelLoaded, key: "face_model" },
      { isLoaded: isModel3dLoaded, key: "3d_model" },
      { isLoaded: isMotion3dLoaded, key: "3d_motion" },
      { isLoaded: isDifyWarmupCompleted, key: "dify_warmup" },
      { isLoaded: isVoicevoxWarmupCompleted, key: "voicevox_warmup" },
      { isLoaded: isAivisspeechWarmupCompleted, key: "aivisspeech_warmup" },
      {
        isLoaded: activationDataStatus === "success",
        key: "activation_and_data",
      },
    ];

    // activation_and_data エラー時は、受付エラーモーダル対象以外を ProgressModal に表示する
    if (activationDataStatus === "error") {
      if (receptionErrorMessage) {
        return;
      }

      if (!completedProcessKeys.has("activation_and_data")) {
        const commonError = errorMessage || t("systemStartupError");
        setErrorMessage(commonError);
      }
      return;
    }

    loadedStatus.forEach(({ isLoaded, key }) => {
      if (isLoaded && !completedProcessKeys.has(key)) {
        completeProcess(key);
      }
    });
  }, [
    isProgressModalOpen,
    isFaceModelLoaded,
    isModel3dLoaded,
    isMotion3dLoaded,
    isDifyWarmupCompleted,
    isVoicevoxWarmupCompleted,
    isAivisspeechWarmupCompleted,
    activationDataStatus,
    activationErrorCode,
    errorMessage,
    receptionErrorMessage,
    completedProcessKeys,
    completeProcess,
    setErrorMessage,
    t,
  ]);

  // set master volume once after progress reaches 100% (only once per modal open)
  const hasSetVolumeRef = useRef(false);
  useEffect(() => {
    if (!isProgressModalOpen) {
      hasSetVolumeRef.current = false;
      return;
    }
    if (progressValue >= 100 && !hasSetVolumeRef.current) {
      setVolume(0.5);
      hasSetVolumeRef.current = true;
    }
  }, [isProgressModalOpen, progressValue, setVolume]);

  // ProgressModalでいずれかのプロセスが失敗した場合は、以降の定期ポーリングを停止
  useEffect(() => {
    if (!progressErrorMessage) return;
    stopPolling();
  }, [progressErrorMessage, stopPolling]);

  useEffect(() => {
    if (activationDataStatus !== "error" || !receptionErrorMessage) {
      return;
    }

    closeProgressModal();
    setIsReceptionErrorModalOpen(true);
  }, [activationDataStatus, closeProgressModal, receptionErrorMessage]);

  /** モデル表示完了時の処理（未使用のため noop にしておく） */
  const handleModelViewed = useCallback(() => {
    // Intentionally left blank — state removed because it was unused.
  }, []);

  /**
   * TTS エンジンに応じてスピーカーIDを取得する
   */
  const getSpeakerIdForEngine = useCallback(
    async (engine: string, characterName: string): Promise<number | null> => {
      if (engine === "VOICEVOX") {
        const config = await db.getVoicevoxConfig(characterName);
        return config?.speaker_id ?? null;
      }
      if (engine === "OpenAI") {
        const config = await db.getOpenaiConfig(characterName);
        return config?.speaker_id ?? null;
      }
      if (engine === "AivisSpeech") {
        const config = await db.getAivisSpeechConfig(characterName);
        return config?.speaker_id ?? null;
      }
      return null;
    },
    []
  );
  const speakerUpdateRequestIdRef = useRef(0);

  /**
   * キャラクター変更の共通ロジック
   * 指定された方向（次へ/前へ）に応じてキャラクターを切り替える
   *
   * @param direction - 切り替え方向（NEXT: 1 または PREVIOUS: -1）
   */
  const changeCharacter = useCallback(
    (direction: DirectionType) => {
      const currentCharacter = getVRMName(currentVRM);
      db.selectAll()
        .then((characters: Character[]) => {
          if (!Array.isArray(characters) || characters.length === 0) {
            console.error("changeCharacter: no characters available");
            return;
          }

          const currentIndex = characters.findIndex((c: Character) => c.name === currentCharacter);
          const newIndex = (currentIndex + direction + characters.length) % characters.length;

          const vrmData = vrmList.find(
            (vrm) => vrm.name === characters[newIndex].global_name + ".vrm"
          );

          if (!vrmData) {
            console.error("changeCharacter: vrmData not found for", characters[newIndex]);
            return;
          }

          setCharacterIndex(newIndex);
          setVRMPath(vrmData.url);

          const newTTSEngine =
            language === currentLocale ? characters[newIndex].tts_engine : "OpenAI";
          setT2SEngine(newTTSEngine);

          // キャラクター変更に伴い、currentSpeakerAtomを更新
          const characterName = characters[newIndex].name;
          const requestId = speakerUpdateRequestIdRef.current + 1;
          speakerUpdateRequestIdRef.current = requestId;

          // 名前は同期的に設定（character dataから）
          setCurrentSpeaker((prev) => ({
            ...prev,
            Name: characterName,
          }));

          // Id/Styleは非同期で取得して更新
          const updateSpeaker = async () => {
            try {
              const speakerId = await getSpeakerIdForEngine(newTTSEngine, characterName);

              // 最新のキャラクター変更リクエストのみ反映する
              if (speakerUpdateRequestIdRef.current !== requestId) {
                return;
              }

              if (speakerId !== null) {
                const info = extractInfo(newTTSEngine, speakerId);
                if (info) {
                  const { name, style } = getNameAndStyle(newTTSEngine, info);
                  setCurrentSpeaker({
                    Id: speakerId,
                    Name: name,
                    Style: style,
                  });
                }
              }
            } catch (err) {
              console.error("changeCharacter: failed to update currentSpeaker", err);
            }
          };
          void updateSpeaker();

          // 成功時のみ効果音を鳴らす
          PlaySound("push", volume);
        })
        .catch((err) => {
          console.error("changeCharacter: failed to load characters", err);
        });
    },
    [
      currentVRM,
      language,
      vrmList,
      setCharacterIndex,
      setVRMPath,
      setT2SEngine,
      setCurrentSpeaker,
      volume,
      getSpeakerIdForEngine,
    ]
  );

  const setNextCharacter = useCallback(() => {
    changeCharacter(characterDirection.next);
  }, [changeCharacter]);

  const setPrevCharacter = useCallback(() => {
    changeCharacter(characterDirection.previous);
  }, [changeCharacter]);

  const stopCharacterButtonMouseUp = useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
  }, []);

  const handlePrevCharacterClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setPrevCharacter();
    },
    [setPrevCharacter]
  );

  const handleNextCharacterClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      setNextCharacter();
    },
    [setNextCharacter]
  );

  // ------ 顔検出 → AI音声ガイダンス（自動遷移しない）→ ボタン押下で遷移 ------

  const hasGreetedForCurrentFaceRef = useRef(false);
  const { playWithCooldown } = usePlayVoiceWithCooldown({
    cooldownMs: 2000,
    endExpression: receptionBowEndExpression(),
  });

  useEffect(() => {
    // Reset greeting gate once face is no longer detected.
    if (!isFace) {
      hasGreetedForCurrentFaceRef.current = false;
    }
  }, [isFace]);

  useEffect(() => {
    if (!isFace || !isFaceDetection || !canNavigate || hasNavigatedRef.current) return;

    // Keep one guidance playback per continuous face-detection session.
    if (hasGreetedForCurrentFaceRef.current) return;

    hasGreetedForCurrentFaceRef.current = true;

    const guidanceText =
      t("receptionStandby.welcomeMessage") + ". " + t("receptionStandby.voiceGuidance");

    void playWithCooldown(guidanceText).catch((err) => console.error("playVoice failed:", err));
  }, [isFace, isFaceDetection, canNavigate, playWithCooldown, t]);

  return (
    <>
      <ProgressModal
        isOpen={isProgressModalOpen && !isReceptionErrorModalOpen}
        progress={progressValue}
        title={t("systemStarting")}
        message={t("systemStartingMessage")}
        errorMessage={progressErrorMessage || errorMessage}
        onClose={closeProgressModal}
      />
      <ReceptionErrorModal
        isOpen={isReceptionErrorModalOpen}
        onClose={() => setIsReceptionErrorModalOpen(false)}
        message={receptionErrorMessage ?? undefined}
      />
      <div className="reception-standby-screen">
        <div className="reception-standby-screen__content">
          <div className="screen__text">
            <h2 className="screen__title">
              <FitText>{t("receptionStandby.welcomeMessage")}</FitText>
            </h2>
            <p className="reception-standby-screen__guidance">
              <FitText>{t("receptionStandby.voiceGuidance")}</FitText>
            </p>
          </div>

          <div className="reception-standby-screen__canvas">
            <Canvas
              key={canvasKey}
              className="reception-standby-screen__canvas-surface"
              camera={cameraConfig}
              flat
              style={canvasStyle}
              gl={glConfig}
            >
              {/* eslint-disable react/no-unknown-property */}
              <directionalLight
                position-x={lighting.directionalLight.position.x}
                position-y={lighting.directionalLight.position.y}
                position-z={lighting.directionalLight.position.z}
                color={lighting.directionalLight.color}
                intensity={lighting.directionalLight.intensity}
              />
              <OrbitControls enableZoom={false} enableRotate={false} enablePan={false} />
              <ambientLight
                color={lighting.ambientLight.color}
                intensity={lighting.ambientLight.intensity}
              />
              {/* eslint-enable react/no-unknown-property */}
              <Model
                onViewed={handleModelViewed}
                idleSwitchProbability={1}
                idleMotionStrategy={idleMotionStrategy}
              />
            </Canvas>

            <div className="reception-standby-screen__character-button">
              <div className="reception-standby-screen__character-button-group">
                <button
                  type="button"
                  className="btn btn-circle"
                  onMouseUp={stopCharacterButtonMouseUp}
                  onClick={handlePrevCharacterClick}
                  disabled={isControlDisabled}
                >
                  <icon.icons_arrow_left />
                </button>
                <button
                  type="button"
                  className="btn btn-circle"
                  onMouseUp={stopCharacterButtonMouseUp}
                  onClick={handleNextCharacterClick}
                  disabled={isControlDisabled}
                >
                  <icon.icons_arrow_right />
                </button>
              </div>
            </div>
          </div>
        </div>

        {isLoading && <ReceptionCommunicatingIndicator />}

        {!isProgressModalOpen &&
          !isReceptionErrorModalOpen &&
          (progressErrorMessage || errorMessage) && (
            <div className="reception-standby-error">{progressErrorMessage || errorMessage}</div>
          )}

        {isFaceDetection && <FaceDetection />}
        <ReceptionStandbyFooter />
      </div>
    </>
  );
};

export default ReceptionStandbyScreen;
